// AGENT-RECORD (docs/direction/2026-10-28-managing-agents-gaps.md §2.1, decision 2): the DE's
// track record, the first tab of its page. One call (`digitalEmployees.record`, 4 / 8 / 12
// weeks): headline tiles for the window, a by-week table whose every non-zero count opens that
// week's evidence in a modal (`digitalEmployees.recordEvidence`, newest first, "Load more"), the
// ROI line (locked until four weeks with outcomes; then the basis, never a ratio) and the money
// incidents (quality incidents are a later build). Fed only from real work: no record, no numbers.
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  InfoTip,
  Segmented,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  WorkerBadge,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import type {
  DeRecord,
  RecordEvidenceItem,
  RecordFigures,
  RecordSignal,
  RecordWeek,
} from "../../api/types";
import { keepFocus } from "../../focus/focus";
import type { CompanyAccess } from "../company";
import { formatDateTime, formatMoney, humanize } from "../format";
import { deRecordEvidenceQuery, deRecordQuery } from "../queries";
import { LoadError, Loading, LoadMore, Nothing } from "../screen-parts";
import { DeHelpers, DeOpsTiles, DeOutcomes, OpsCaption } from "./de-ops";

const WINDOWS = ["4", "8", "12"] as const;
type Window = (typeof WINDOWS)[number];

/** A share as a whole percent; null (nothing reviewed) is "—", never 0%. */
export function formatRate(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

/** Supervisor minutes per outcome: "12.5 min", "1 h 5 min"; null is "—". */
export function formatMinutes(minutes: number | null): string {
  if (minutes === null) return "—";
  if (minutes < 60) return `${Math.round(minutes * 10) / 10} min`;
  // Whole minutes first, so 119.5 reads 2 h, never 1 h 60 min.
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

const perOutcome = (minor: number | null, currency: string) =>
  minor === null ? "—" : formatMoney(minor, currency);

const SIGNAL_LABELS: Record<RecordSignal, string> = {
  approved_as_is: "Approved as-is",
  edited: "Edited",
  rejected: "Rejected",
  completed: "Completed",
  work_outcome: "Outcomes",
  reported_outcome: "Reported outcomes",
  intervention: "Interventions",
  incident: "Incidents",
  run: "Runs",
  decided: "Decided in run",
  escalated: "Escalated",
  reversed: "Reversed",
};

/** The by-week table's count columns: the figure, and the evidence signal it opens. */
const COUNT_COLUMNS: { field: keyof RecordFigures; signal: RecordSignal; label: string }[] = [
  { field: "runs", signal: "run", label: "Runs" },
  { field: "approved_as_is", signal: "approved_as_is", label: "As-is" },
  { field: "edited", signal: "edited", label: "Edited" },
  { field: "rejected", signal: "rejected", label: "Rejected" },
  { field: "completed", signal: "completed", label: "Completed" },
  { field: "outcomes", signal: "work_outcome", label: "Outcomes" },
  { field: "reported_outcomes", signal: "reported_outcome", label: "Reported" },
  { field: "interventions", signal: "intervention", label: "Interventions" },
  { field: "incidents", signal: "incident", label: "Incidents" },
  // DECISION-LEDGER: the decision trail (each opens its evidence: who, and why for a reversal)
  { field: "decided", signal: "decided", label: "Decided" },
  { field: "escalated", signal: "escalated", label: "Escalated" },
  { field: "reversed", signal: "reversed", label: "Reversed" },
];

export function RecordTab({ api, company, id }: { api: Api; company: CompanyAccess; id: string }) {
  const [span, setSpan] = useState<Window>("8");
  const record = useQuery(deRecordQuery(api, company.id, id, Number(span)));
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="de-record">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <WorkerBadge kind="de" />
          Track record
          <InfoTip label="About the record">
            Counted only from real work: approvals, completed tasks, reported outcomes, take-overs,
            cost and approved supervision time.
          </InfoTip>
        </span>
        {record.data?.has_data === false || record.isError ? null : (
          <Segmented
            aria-label="Weeks shown"
            size="sm"
            value={span}
            onValueChange={setSpan}
            options={WINDOWS.map((w) => ({ value: w, label: `${w} wks` }))}
          />
        )}
      </div>
      {record.isPending ? (
        <Loading label="Loading the record" rows={4} />
      ) : record.isError ? (
        <LoadError what="The record" error={record.error} />
      ) : (
        <RecordView record={record.data} api={api} company={company} id={id} />
      )}
    </div>
  );
}

/** The record once loaded (the empty state, or the tiles, ROI line and weeks). */
export function RecordView({
  record,
  api,
  company,
  id,
}: {
  record: DeRecord;
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const [open, setOpen] = useState<{ week: string; signal: RecordSignal } | null>(null);
  // DE-KPIS-LIVE: activity, KPIs and cost together, then the helpers under the DE. Shown even
  // before the record has reviewed work, so the budget and the helpers are never hidden.
  const ops = (
    <>
      <section className="flex flex-col gap-2" aria-label="Outcomes">
        <OpsCaption
          label="Outcomes"
          info="The outcome KPIs of its role, set for every DE of the role and editable on its seat. CompanyOS counts what it sees happen (deliveries, messages, briefs, issues, meetings, tasks, reviews); a KPI only the DE can report reads 'Not tracked yet' until it does."
        />
        <DeOutcomes record={record} />
      </section>
      <section className="flex flex-col gap-2" aria-label="Runs and cost">
        <OpsCaption
          label="Runs and cost"
          info="Fed from the DE's own runs and the cost ledger as they happen; nobody enters these."
        />
        <DeOpsTiles record={record} onOpenRuns={(week) => setOpen({ week, signal: "run" })} />
      </section>
      <section className="flex flex-col gap-1" aria-label="Helpers">
        <OpsCaption
          label="Helpers"
          info="The agents (AG) this DE leads or uses, with what their model calls cost in these weeks."
        />
        <DeHelpers helpers={record.helpers} currency={record.currency} />
      </section>
    </>
  );
  // With no record at all, the tiles only show when there is a budget or a helper to see.
  const opsWhenEmpty =
    record.budget || record.helpers.length > 0 || record.outcomes.length > 0 ? ops : null;
  if (!record.has_data)
    return (
      <>
        {opsWhenEmpty}
        <Nothing title="No record yet">
          Fills from approvals, completed tasks, take-overs and cost as the DE works.
        </Nothing>
      </>
    );
  if (!record.window_has_data)
    return (
      <>
        {ops}
        <Nothing title="Nothing in these weeks">Pick more weeks to see the older record.</Nothing>
      </>
    );
  return (
    <>
      {ops}
      <Tiles record={record} />
      <Roi record={record} />
      <Weeks record={record} onOpen={(week, signal) => setOpen({ week, signal })} />
      {open ? (
        <EvidenceDialog
          api={api}
          company={company}
          id={id}
          week={open.week}
          signal={open.signal}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </>
  );
}

function Tile({
  label,
  value,
  field,
  sub,
  info,
}: {
  label: string;
  value: string;
  field: string;
  sub?: ReactNode;
  info?: ReactNode;
}) {
  return (
    <div
      className="flex min-w-0 flex-col gap-1 rounded-md border border-border p-3"
      data-tile={field}
    >
      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
        {label}
        {info ? <InfoTip label={`About ${label}`}>{info}</InfoTip> : null}
      </span>
      <span className="text-xl font-semibold tabular-nums" data-field="value">
        {value}
      </span>
      {sub ? <span className="text-xs text-muted-foreground tabular-nums">{sub}</span> : null}
    </div>
  );
}

function Tiles({ record }: { record: DeRecord }) {
  const t = record.total;
  const by = record.interventions_by_kind;
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-slot="record-tiles">
      <Tile
        label="Approved as-is"
        field="approved_as_is"
        value={formatRate(t.approved_as_is_rate)}
        sub={`${t.approved_as_is} outputs`}
        info="Share of reviewed outputs approved without changes."
      />
      <Tile
        label="Edited"
        field="edited"
        value={formatRate(t.edited_rate)}
        sub={`${t.edited} outputs`}
      />
      <Tile
        label="Rejected"
        field="rejected"
        value={formatRate(t.rejected_rate)}
        sub={`${t.rejected} outputs`}
      />
      <Tile
        label="Cost per outcome"
        field="cost_per_outcome"
        value={perOutcome(t.cost_per_outcome_minor, record.currency)}
        sub={`${t.outcomes} outcomes`}
        info="Client-visible cost divided by work outcomes: outputs approved (as-is or edited) and tasks completed."
      />
      <Tile
        label="Supervisor time / outcome"
        field="supervisor_per_outcome"
        value={formatMinutes(t.supervisor_minutes_per_outcome)}
        info="Approved supervision time on the DE's seat, divided by outcomes."
      />
      <Tile
        label="Interventions"
        field="interventions"
        value={String(t.interventions)}
        sub={`Take-over ${by.take_over ?? 0} · Cancel ${by.cancel ?? 0}`}
        info="A person took over the DE's task or room, or cancelled its run."
      />
      <Tile
        label="Incidents"
        field="incidents"
        value={String(t.incidents)}
        info={
          record.incidents_tracked.includes("budget")
            ? "Money (budget) incidents. Quality incidents are not tracked yet."
            : "Quality incidents are not tracked yet."
        }
      />
    </div>
  );
}

function Roi({ record }: { record: DeRecord }) {
  const roi = record.roi;
  if (roi.state === "locked")
    return (
      <p
        className="inline-flex items-center gap-1 text-sm"
        data-slot="record-roi"
        data-state="locked"
      >
        <span>
          ROI: after {roi.weeks_needed} weeks of outcomes ·{" "}
          <span className="tabular-nums" data-field="progress">
            {roi.weeks_with_outcomes}/{roi.weeks_needed}
          </span>
        </span>
        <InfoTip label="About ROI">
          No ROI figure until {roi.weeks_needed} weeks have outcomes; until then, read cost and
          supervisor time per outcome.
        </InfoTip>
      </p>
    );
  return (
    <p
      className="inline-flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
      data-slot="record-roi"
      data-state="ready"
    >
      <span className="font-medium">ROI basis, last {roi.weeks_needed} weeks</span>
      <span data-field="basis-cost">
        Cost per outcome {perOutcome(roi.basis.cost_per_outcome_minor, record.currency)}
      </span>
      <span data-field="basis-supervisor">
        Supervisor time / outcome {formatMinutes(roi.basis.supervisor_minutes_per_outcome)}
      </span>
      <InfoTip label="About the ROI basis">The human seat comparison comes from Finance.</InfoTip>
    </p>
  );
}

function Weeks({
  record,
  onOpen,
}: {
  record: DeRecord;
  onOpen: (week: string, signal: RecordSignal) => void;
}) {
  const last = record.weeks.at(-1)?.week;
  return (
    <div className="min-w-0 overflow-x-auto">
      <Table aria-label="Record by week, oldest first">
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Week</TableHead>
            {COUNT_COLUMNS.map((c) => (
              <TableHead key={c.field} scope="col" className="text-right">
                {c.label}
              </TableHead>
            ))}
            <TableHead scope="col" className="text-right">
              As-is %
            </TableHead>
            <TableHead scope="col" className="text-right">
              Edited %
            </TableHead>
            <TableHead scope="col" className="text-right">
              Rejected %
            </TableHead>
            <TableHead scope="col" className="text-right">
              Cost / outcome
            </TableHead>
            <TableHead scope="col" className="text-right">
              Supervisor / outcome
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {record.weeks.map((w) => (
            <WeekRow
              key={w.week}
              w={w}
              current={w.week === last}
              currency={record.currency}
              onOpen={onOpen}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function WeekRow({
  w,
  current,
  currency,
  onOpen,
}: {
  w: RecordWeek;
  current: boolean;
  currency: string;
  onOpen: (week: string, signal: RecordSignal) => void;
}) {
  const short = w.week.slice(5);
  return (
    <TableRow data-week={w.week}>
      <TableHead
        scope="row"
        className="font-normal whitespace-nowrap"
        title={`${w.starts_on} – ${w.ends_on}`}
      >
        {short}
        {current ? <span className="text-muted-foreground"> (now)</span> : null}
      </TableHead>
      {COUNT_COLUMNS.map((c) => {
        const n = w[c.field] as number;
        return (
          <TableCell key={c.field} className="text-right tabular-nums" data-field={c.field}>
            {n > 0 ? (
              <button
                type="button"
                className="rounded-sm px-1 text-primary-text underline underline-offset-4 hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={`${SIGNAL_LABELS[c.signal]}, ${short}: ${n}`}
                onClick={() => onOpen(w.week, c.signal)}
              >
                {n}
              </button>
            ) : (
              <span className="text-muted-foreground">0</span>
            )}
          </TableCell>
        );
      })}
      <TableCell className="text-right tabular-nums" data-field="approved_as_is_rate">
        {formatRate(w.approved_as_is_rate)}
      </TableCell>
      <TableCell className="text-right tabular-nums" data-field="edited_rate">
        {formatRate(w.edited_rate)}
      </TableCell>
      <TableCell className="text-right tabular-nums" data-field="rejected_rate">
        {formatRate(w.rejected_rate)}
      </TableCell>
      <TableCell className="text-right tabular-nums" data-field="cost_per_outcome">
        {perOutcome(w.cost_per_outcome_minor, currency)}
      </TableCell>
      <TableCell className="text-right tabular-nums" data-field="supervisor_per_outcome">
        {formatMinutes(w.supervisor_minutes_per_outcome)}
      </TableCell>
    </TableRow>
  );
}

/** One week's evidence for one signal (SEAT-DRAWER-UX: the seat drawer's Record tiles open it). */
export function EvidenceDialog({
  api,
  company,
  id,
  week,
  signal,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  week: string;
  signal: RecordSignal;
  onClose: () => void;
}) {
  const evidence = useInfiniteQuery(deRecordEvidenceQuery(api, company.id, id, week, signal));
  const items = evidence.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-slot="record-evidence">
        <DialogHeader>
          <DialogTitle>
            {SIGNAL_LABELS[signal]} · {week.slice(5)}
          </DialogTitle>
        </DialogHeader>
        {evidence.isPending ? (
          <Loading label="Loading the evidence" rows={3} />
        ) : evidence.isError ? (
          <LoadError what="The evidence" error={evidence.error} />
        ) : !items.length ? (
          <p className="text-sm text-muted-foreground">Nothing recorded.</p>
        ) : (
          <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
            <ol
              aria-label="Evidence, newest first"
              className="flex flex-col divide-y divide-border"
            >
              {items.map((item) => (
                <EvidenceLine key={item.id} item={item} slug={company.slug} />
              ))}
            </ol>
            <LoadMore
              what="evidence"
              hasMore={evidence.hasNextPage}
              loading={evidence.isFetchingNextPage}
              onMore={() => void evidence.fetchNextPage()}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** What the line is about, linked where the app has a page for it. */
function Subject({ item, slug }: { item: RecordEvidenceItem; slug: string }) {
  const s = item.subject;
  const cls = "text-primary-text underline-offset-4 hover:underline";
  if (s?.kind === "approval")
    return (
      <Link
        to="/c/$slug/agents/approvals/$id"
        params={{ slug, id: s.id }}
        search={keepFocus}
        className={cls}
      >
        Approval
      </Link>
    );
  if (s?.kind === "task")
    return (
      <Link
        to="/c/$slug/work/t/$taskId"
        params={{ slug, taskId: s.id }}
        search={keepFocus}
        className={cls}
      >
        Task
      </Link>
    );
  const run = s?.kind === "run" ? s.id : item.run_id;
  if (run)
    return (
      <Link
        to="/c/$slug/agents/runs/$id"
        params={{ slug, id: run }}
        search={keepFocus}
        className={cls}
      >
        Run
      </Link>
    );
  return s ? <span>{humanize(s.kind)}</span> : null;
}

function EvidenceLine({ item, slug }: { item: RecordEvidenceItem; slug: string }) {
  return (
    <li
      className="flex flex-col gap-1 py-2 sm:flex-row sm:items-start sm:gap-4"
      data-evidence={item.id}
    >
      <time dateTime={item.at} className="w-32 shrink-0 text-xs text-muted-foreground">
        {formatDateTime(item.at)}
      </time>
      <span className="flex min-w-0 flex-wrap items-center gap-x-2 text-sm">
        <Subject item={item} slug={slug} />
        {item.detail ? <span>{humanize(item.detail)}</span> : null}
        {item.by_name ? <span className="text-muted-foreground">by {item.by_name}</span> : null}
        {item.reason ? (
          <span className="w-full text-muted-foreground" data-field="reason">
            “{item.reason}”
          </span>
        ) : null}
      </span>
    </li>
  );
}
